import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useRealtimeSubscription } from '../lib/realtime';
import { canonicalMoney, sumMoney } from '../lib/money';
import type { RevenueData, JobStats, BookingStats, TeamUtilization, ReportRange } from '../types';

function formatDate(d: Date): string { return d.toISOString().split('T')[0]; }

function getRangeDates(range: ReportRange): { start: Date; end: Date } {
  const end = new Date(); end.setHours(23, 59, 59, 999);
  const start = new Date();
  if (range === 'week') { start.setDate(start.getDate() - 6); }
  else if (range === 'month') { start.setMonth(start.getMonth() - 1); }
  else if (range === 'quarter') { start.setMonth(start.getMonth() - 3); }
  else { start.setFullYear(start.getFullYear() - 1); }
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

function timeToHours(t: string): number { const [h, m] = t.split(':').map(Number); return h + m / 60; }
function tsToHours(iso: string): number { const t = iso?.includes('T') ? iso.split('T')[1] : iso ?? '0:0'; return timeToHours(t); }

interface UseReportsResult {
  revenue: RevenueData;
  jobStats: JobStats;
  bookingStats: BookingStats;
  teamUtilization: TeamUtilization[];
  dateRange: { start: Date; end: Date };
  range: ReportRange;
  setRange: (r: ReportRange) => void;
  loading: boolean;
  exportCSV: () => void;
}

const emptyRevenue: RevenueData = { totalRevenue: 0, invoiceRevenue: 0, bookingRevenue: 0, revenueByDay: [], revenueByService: [], revenueByClient: [] };
const emptyJobs: JobStats = { totalJobs: 0, completed: 0, completionRate: 0, avgCompletionDays: 0, byStatus: [] };
const emptyBookings: BookingStats = { totalBookings: 0, completed: 0, cancelled: 0, noShow: 0, completionRate: 0, cancellationRate: 0, noShowRate: 0, revenueFromBookings: 0 };

export function useReports(businessId: string | undefined): UseReportsResult {
  const [range, setRange] = useState<ReportRange>('month');
  const [revenue, setRevenue] = useState<RevenueData>(emptyRevenue);
  const [jobStats, setJobStats] = useState<JobStats>(emptyJobs);
  const [bookingStats, setBookingStats] = useState<BookingStats>(emptyBookings);
  const [teamUtilization, setTeamUtilization] = useState<TeamUtilization[]>([]);
  const [loading, setLoading] = useState(true);

  const dateRange = useMemo(() => getRangeDates(range), [range]);

  const fetchAll = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    const startStr = formatDate(dateRange.start);
    const endStr = formatDate(dateRange.end);

    try {
      const [invoicesRes, bookingsRes, workOrdersRes, usersRes, shiftsRes, servicesRes] = await Promise.all([
        supabase.from('invoices')
          .select('id, total, status, paid_at, client_name, created_at')
          .eq('business_id', businessId).eq('status', 'paid')
          .gte('paid_at', startStr).lte('paid_at', endStr + 'T23:59:59'),

        supabase.from('bookings')
          .select('id, service_id, client_name, assigned_id, status, payment_status, payment_amount, date')
          .eq('business_id', businessId).gte('date', startStr).lte('date', endStr),

        supabase.from('work_orders')
          .select('id, title, status, assigned_id, scheduled_date, completed_at, created_at')
          .eq('business_id', businessId).gte('scheduled_date', startStr).lte('scheduled_date', endStr),

        supabase.from('users')
          .select('id, first_name, last_name, employment_type')
          .eq('business_id', businessId).eq('status', 'active'),

        supabase.from('schedule_entries')
          .select('assigned_to, start_at, end_at, status')
          .eq('business_id', businessId).eq('type', 'shift')
          .gte('start_at', `${startStr}T00:00:00`).lte('start_at', `${endStr}T23:59:59`)
          .neq('status', 'cancelled'),

        supabase.from('services')
          .select('id, name').eq('business_id', businessId),
      ]);

      const invoices = invoicesRes.data ?? [];
      const bookings = bookingsRes.data ?? [];
      const workOrders = workOrdersRes.data ?? [];
      const users = usersRes.data ?? [];
      const shifts = shiftsRes.data ?? [];
      const services = servicesRes.data ?? [];
      const serviceMap = new Map(services.map(s => [s.id, s.name]));

      // ── Revenue — Rule #34 canonical sums ──
      const invoiceRevenue = Number(sumMoney(invoices.map(i => canonicalMoney(i.total ?? 0))));
      const paidStatuses = ['paid_cash', 'paid_card', 'paid_etransfer'];
      const paidBookings = bookings.filter(b => paidStatuses.includes(b.payment_status));
      const bookingRevenue = Number(sumMoney(paidBookings.map(b => canonicalMoney(b.payment_amount ?? 0))));

      // Revenue by day
      const dayMap = new Map<string, number>();
      for (const inv of invoices) {
        if (inv.paid_at) { const d = inv.paid_at.split('T')[0]; dayMap.set(d, (dayMap.get(d) ?? 0) + (inv.total ?? 0)); }
      }
      for (const bk of paidBookings) { dayMap.set(bk.date, (dayMap.get(bk.date) ?? 0) + (bk.payment_amount ?? 0)); }
      const revenueByDay = [...dayMap.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, amount]) => ({ date, amount: Math.round(amount * 100) / 100 }));

      // Revenue by service (from bookings)
      const svcMap = new Map<string, number>();
      for (const bk of paidBookings) {
        const name = bk.service_id ? (serviceMap.get(bk.service_id) ?? 'Other') : 'Other';
        svcMap.set(name, (svcMap.get(name) ?? 0) + (bk.payment_amount ?? 0));
      }
      const revenueByService = [...svcMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([service, amount]) => ({ service, amount: Math.round(amount) }));

      // Revenue by client
      const clientMap = new Map<string, number>();
      for (const inv of invoices) { const c = inv.client_name ?? 'Unknown'; clientMap.set(c, (clientMap.get(c) ?? 0) + (inv.total ?? 0)); }
      for (const bk of paidBookings) { const c = bk.client_name ?? 'Unknown'; clientMap.set(c, (clientMap.get(c) ?? 0) + (bk.payment_amount ?? 0)); }
      const revenueByClient = [...clientMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10).map(([client, amount]) => ({ client, amount: Math.round(amount) }));

      setRevenue({ totalRevenue: invoiceRevenue + bookingRevenue, invoiceRevenue, bookingRevenue, revenueByDay, revenueByService, revenueByClient });

      // ── Jobs ──
      const completedWO = workOrders.filter(w => w.status === 'completed');
      const statusCounts = new Map<string, number>();
      for (const w of workOrders) statusCounts.set(w.status, (statusCounts.get(w.status) ?? 0) + 1);
      let totalDays = 0; let daysCount = 0;
      for (const w of completedWO) {
        if (w.completed_at && w.created_at) {
          const days = (new Date(w.completed_at).getTime() - new Date(w.created_at).getTime()) / 86400000;
          totalDays += days; daysCount++;
        }
      }
      setJobStats({
        totalJobs: workOrders.length, completed: completedWO.length,
        completionRate: workOrders.length > 0 ? Math.round((completedWO.length / workOrders.length) * 100) : 0,
        avgCompletionDays: daysCount > 0 ? Math.round((totalDays / daysCount) * 10) / 10 : 0,
        byStatus: [...statusCounts.entries()].map(([status, count]) => ({ status, count })),
      });

      // ── Bookings ──
      const completedBk = bookings.filter(b => b.status === 'completed');
      const cancelledBk = bookings.filter(b => b.status === 'cancelled');
      const noShowBk = bookings.filter(b => b.status === 'no_show');
      const totalBk = bookings.length;
      setBookingStats({
        totalBookings: totalBk, completed: completedBk.length,
        cancelled: cancelledBk.length, noShow: noShowBk.length,
        completionRate: totalBk > 0 ? Math.round((completedBk.length / totalBk) * 100) : 0,
        cancellationRate: totalBk > 0 ? Math.round((cancelledBk.length / totalBk) * 100) : 0,
        noShowRate: totalBk > 0 ? Math.round((noShowBk.length / totalBk) * 100) : 0,
        revenueFromBookings: bookingRevenue,
      });

      // ── Team utilization ──
      const daysInRange = Math.max(1, Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / 86400000));
      const availableH = Math.min(daysInRange, daysInRange * 5 / 7) * 8; // ~8h per working day
      const shiftsByUser = new Map<string, number>();
      for (const s of shifts) {
        const startH = tsToHours(s.start_at);
        const endH = tsToHours(s.end_at);
        const h = Math.max(0, endH - startH);
        const uid = Array.isArray(s.assigned_to) ? s.assigned_to[0] : null;
        if (uid) shiftsByUser.set(uid, (shiftsByUser.get(uid) ?? 0) + h);
      }
      const jobsByUser = new Map<string, number>();
      for (const w of completedWO) { if (w.assigned_id) jobsByUser.set(w.assigned_id, (jobsByUser.get(w.assigned_id) ?? 0) + 1); }
      const bkByUser = new Map<string, number>();
      for (const b of completedBk) { if (b.assigned_id) bkByUser.set(b.assigned_id, (bkByUser.get(b.assigned_id) ?? 0) + 1); }

      const util: TeamUtilization[] = users.map(u => {
        const sched = shiftsByUser.get(u.id) ?? 0;
        return {
          userId: u.id, name: `${u.first_name} ${u.last_name}`,
          employmentType: u.employment_type,
          scheduledHours: Math.round(sched * 10) / 10,
          availableHours: Math.round(availableH * 10) / 10,
          utilizationRate: availableH > 0 ? Math.round((sched / availableH) * 100) : 0,
          jobsCompleted: jobsByUser.get(u.id) ?? 0,
          bookingsCompleted: bkByUser.get(u.id) ?? 0,
        };
      }).sort((a, b) => b.utilizationRate - a.utilizationRate);
      setTeamUtilization(util);
    } catch { /* keep existing state */ } finally { setLoading(false); }
  }, [businessId, dateRange]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  const rtTables = useMemo(() => ['invoices', 'work_orders', 'bookings'], []);
  useRealtimeSubscription(businessId, rtTables, fetchAll);

  const exportCSV = useCallback(() => {
    const lines = [
      `CrewBit Report — ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`,
      '', '--- REVENUE ---',
      `Total Revenue,$${revenue.totalRevenue}`,
      `Invoice Revenue,$${revenue.invoiceRevenue}`,
      `Booking Revenue,$${revenue.bookingRevenue}`,
      '', '--- JOBS ---',
      `Total,${jobStats.totalJobs}`,
      `Completed,${jobStats.completed}`,
      `Completion Rate,${jobStats.completionRate}%`,
      '', '--- BOOKINGS ---',
      `Total,${bookingStats.totalBookings}`,
      `Completed,${bookingStats.completed}`,
      `Cancelled,${bookingStats.cancelled}`,
      `No-show,${bookingStats.noShow}`,
      '', '--- TEAM UTILIZATION ---',
      'Name,Type,Scheduled Hours,Available Hours,Utilization %,Jobs Done,Bookings Done',
      ...teamUtilization.map(t => `"${t.name}",${t.employmentType},${t.scheduledHours},${t.availableHours},${t.utilizationRate}%,${t.jobsCompleted},${t.bookingsCompleted}`),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `crewbit-report-${formatDate(new Date())}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [revenue, jobStats, bookingStats, teamUtilization, dateRange]);

  return { revenue, jobStats, bookingStats, teamUtilization, dateRange, range, setRange, loading, exportCSV };
}
